/**
 * Route prefix for the storefront. Storefront pages live under the
 * `app/blu/` route directory (served at `/blu/*`), while the site root `/`
 * and API routes (`/api/*`) live outside it.
 */
export const STOREFRONT_BASE = "/blu";

/** Storefront landing page; the site root `/` serves the combined experience. */
export const STOREFRONT_HOME_PAGE = STOREFRONT_BASE;

export const SHOP_PAGE = `${STOREFRONT_BASE}/shop`;
export const BUILD_MY_SYSTEM_PAGE = `${SHOP_PAGE}/build-my-system`;

export const CART_PAGE = `${STOREFRONT_BASE}/cart`;
export const CHECKOUT_PAGE = `${STOREFRONT_BASE}/checkout`;
export const PAYMENT_PAGE = `${CHECKOUT_PAGE}/payment`;
export const CREDIT_CARD_PAYMENT_PAGE = `${PAYMENT_PAGE}/credit-card`;
export const AFFIRM_PAYMENT_PAGE = `${PAYMENT_PAGE}/affirm`;
export const PAYMENT_MONITORING_PAGE = `${PAYMENT_PAGE}/monitoring`;
export const TERMS_OF_SERVICE_PAGE = `${CHECKOUT_PAGE}/terms-of-service`;
export const CONFIRMATION_PAGE = `${STOREFRONT_BASE}/confirmation`;

export const QUIZ_PAGE = `${STOREFRONT_BASE}/quiz`;
export const QUIZ_RESULTS_PAGE = `${QUIZ_PAGE}/results`;

export const MONITORING_PAGE = `${STOREFRONT_BASE}/monitoring`;

/** Resources hub. Sits at the site root, outside the storefront prefix. */
export const RESOURCES_PAGE = "/resources";

/**
 * Rebuilds the stored slug path for a `/resources` URL segment. Articles and article categories both store their full app path.
 */
export const resourcePath = (segment: string) => `${RESOURCES_PAGE}/${segment}`;

/** Local hub. Sits at the site root, outside the storefront prefix. */
export const LOCAL_PAGE = "/local";

/** Legal hub. The index is this path; child pages nest under it. */
export const LEGAL_PAGE = "/about-adt/legal";

/** Rebuilds the stored slug path for a `/about-adt/legal` URL. */
export const legalPagePath = (segments?: string[]) =>
  segments?.length ? `${LEGAL_PAGE}/${segments.join("/")}` : LEGAL_PAGE;

/** Health index. Child pages nest under it. */
export const HEALTH_PAGE = "/health";

/** Rebuilds the stored slug path for a `/health` URL. */
export const healthPagePath = (segments?: string[]) =>
  segments?.length ? `${HEALTH_PAGE}/${segments.join("/")}` : HEALTH_PAGE;

/**
 * Rebuilds the stored slug path for a `/local` URL segment. Local state pages store their full app path.
 */
export const localStatePath = (segment: string) => `${LOCAL_PAGE}/${segment}`;

/**
 * Rebuilds the stored slug path for a `/local/{state}/{city}` URL. Local city pages store their full app path.
 */
export const localCityPath = (state: string, city: string) =>
  `${LOCAL_PAGE}/${state}/${city}`;

/** Sanity document ID backing the quiz flow. */
export const QUIZ_DOCUMENT_ID = "quiz";

/**
 * Product detail page for an SFCC product id. Matches the product's stored
 * slug path by construction — use this when only an id is at hand (e.g.
 * basket line items); products fetched from Sanity carry the path as `slug`.
 */
export const productPage = (id: string) => `${SHOP_PAGE}/${id}`;

/** Shop page filtered to a single category. */
export const shopCategoryPage = (category: string) =>
  `${SHOP_PAGE}?category=${category}`;

/**
 * Next.js route patterns for `revalidatePath`. Dynamic segments stay literal
 * (`[slug]`), so these are not navigable URLs.
 */
export const SHOP_PDP_REVALIDATE_PATH = `${SHOP_PAGE}/[slug]`;
